import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Select, Typography, Alert, Spin, Space, Flex, App as AntApp,
} from 'antd';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const TRIMESTRES = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
type Trim = typeof TRIMESTRES[number];
const LABELS_TRIM: Record<Trim, string> = {
  Q1: 'Q1 (Jan–Mar)', Q2: 'Q2 (Abr–Jun)', Q3: 'Q3 (Jul–Set)', Q4: 'Q4 (Out–Dez)',
};

const AREAS_RECEITA = [
  { key: 'topografia',   label: 'Topografia' },
  { key: 'hidrometria',  label: 'Hidrometria' },
  { key: 'consultoria',  label: 'Consultoria Ambiental' },
  { key: 'projeto',      label: 'Projeto' },
  { key: 'fiscalizacao', label: 'Fiscalização de Projeto' },
  { key: 'segbarr',      label: 'Segurança de Barragens' },
];

const GRUPOS_ORDEM = [
  { grupo: 'COMISSOES',                    label: 'Comissões de Vendas' },
  { grupo: 'CUSTOS_OPERACIONAIS',          label: 'Custos Operacionais' },
  { grupo: 'DESPESAS_COMERCIAIS',          label: 'Despesas Comerciais e de Marketing' },
  { grupo: 'DESPESAS_ADMINISTRATIVAS',     label: 'Despesas Administrativas' },
  { grupo: 'OUTRAS_DESPESAS_OPERACIONAIS', label: 'Outras Despesas Operacionais' },
  { grupo: 'DESPESAS_FINANCEIRAS',         label: 'Despesas Financeiras' },
  { grupo: 'RECEITAS_FINANCEIRAS',         label: 'Receitas Financeiras' },
  { grupo: 'IMPOSTOS',                     label: 'Impostos' },
  { grupo: 'INVESTIMENTOS',               label: 'Investimentos em Imobilizado' },
];

const RESULTADO_APOS: Record<string, { key: string; label: string }> = {
  COMISSOES:                    { key: 'recLiqComercial', label: '= Receita Líquida Comercial' },
  CUSTOS_OPERACIONAIS:          { key: 'lucroBruto',      label: '= Lucro Bruto' },
  OUTRAS_DESPESAS_OPERACIONAIS: { key: 'resultadoOp',     label: '= Resultado Operacional' },
  RECEITAS_FINANCEIRAS:         { key: 'resultadoAntes',  label: '= Resultado Antes dos Tributos' },
  IMPOSTOS:                     { key: 'lucroLiq',        label: '= Lucro Líquido' },
  INVESTIMENTOS:                { key: 'lucroFinal',      label: '= Lucro Final (Resultado do Exercício)' },
};

const fmt = (v: number) => {
  const abs = Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return v < 0 ? `(R$ ${abs})` : `R$ ${abs}`;
};

type Valores   = Record<string, Record<string, number>>;
type SaveState = Record<string, 'idle' | 'saving' | 'ok' | 'err'>;
type GrupoCats = Record<string, string[]>;

function calcularDerivados(vals: Valores, gc: GrupoCats, q: string): Record<string, number> {
  const v = vals[q] || {};
  const sub = (grupo: string) => (gc[grupo] || []).reduce((s, l) => s + (v[l] ?? 0), 0);
  const recBruta        = AREAS_RECEITA.reduce((s, a) => s + (v[a.key] ?? 0), 0);
  const comissoes       = sub('COMISSOES');
  const recLiqComercial = recBruta + comissoes;
  const custosOp        = sub('CUSTOS_OPERACIONAIS');
  const lucroBruto      = recLiqComercial + custosOp;
  const despComercial   = sub('DESPESAS_COMERCIAIS');
  const despAdmin       = sub('DESPESAS_ADMINISTRATIVAS');
  const outrasDespOp    = sub('OUTRAS_DESPESAS_OPERACIONAIS');
  const resultadoOp     = lucroBruto + despComercial + despAdmin + outrasDespOp;
  const despFin         = sub('DESPESAS_FINANCEIRAS');
  const recFin          = sub('RECEITAS_FINANCEIRAS');
  const resultadoAntes  = resultadoOp + despFin + recFin;
  const impostos        = sub('IMPOSTOS');
  const lucroLiq        = resultadoAntes + impostos;
  const investimentos   = sub('INVESTIMENTOS');
  const lucroFinal      = lucroLiq + investimentos;
  return {
    recBruta, comissoes, recLiqComercial, custosOp, lucroBruto,
    despComercial, despAdmin, outrasDespOp, resultadoOp,
    despFin, recFin, resultadoAntes, impostos, lucroLiq, investimentos, lucroFinal,
  };
}

function ResultRow({ label, valores, calcKey }: { label: string; valores: Valores; calcKey: string }) {
  const total = TRIMESTRES.reduce((s, q) => s + (valores[q]?.[calcKey] ?? 0), 0);
  return (
    <tr style={{ borderTop: '2px solid #0930D0', borderBottom: '2px solid #0930D0', background: '#EDF0FF' }}>
      <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: '#0930D0' }}>{label}</td>
      {TRIMESTRES.map(q => {
        const val = valores[q]?.[calcKey] ?? 0;
        return (
          <td key={q} style={{ textAlign: 'right', padding: '5px 10px', fontSize: 13, fontWeight: 700, color: val >= 0 ? '#0930D0' : '#E24B4A' }}>
            {val === 0 ? '—' : fmt(val)}
          </td>
        );
      })}
      <td style={{ textAlign: 'right', padding: '5px 10px', fontSize: 13, fontWeight: 700, color: total >= 0 ? '#0930D0' : '#E24B4A' }}>
        {total === 0 ? '—' : fmt(total)}
      </td>
    </tr>
  );
}

function OrcamentoInner() {
  const [ano, setAno]             = useState(2026);
  const [valores, setValores]     = useState<Valores>({});
  const [grupoCats, setGrupoCats] = useState<GrupoCats>({});
  const [editando, setEditando]   = useState<{ trim: string; key: string } | null>(null);
  const [inputVal, setInputVal]   = useState('');
  const [saveState, setSaveState] = useState<SaveState>({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]           = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { carregarTudo(); }, [ano]); // eslint-disable-line
  useEffect(() => {
    if (editando) setTimeout(() => inputRef.current?.focus(), 0);
  }, [editando]);

  async function carregarTudo() {
    setCarregando(true);
    setErro('');
    try {
      const token = await getToken();
      const catRes = await axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } });
      const cats: any[] = catRes.data.body ? JSON.parse(catRes.data.body) : catRes.data;
      const gc: GrupoCats = {};
      const seen = new Set<string>();
      for (const c of cats) {
        if (!c.linha_dre || seen.has(c.linha_dre)) continue;
        seen.add(c.linha_dre);
        if (!gc[c.grupo_dre]) gc[c.grupo_dre] = [];
        gc[c.grupo_dre].push(c.linha_dre);
      }
      setGrupoCats(gc);

      const novos: Valores = {};
      await Promise.all(TRIMESTRES.map(async (q) => {
        const t = encodeURIComponent(`${ano}/${q}`);
        const res = await axios.get(`${API_URL}/orcamento?trimestre=${t}`, { headers: { Authorization: token } });
        const rows: any[] = res.data.body ? JSON.parse(res.data.body) : res.data;
        novos[q] = {};
        for (const r of rows) novos[q][r.linha_dre] = parseFloat(r.valor_orcado) || 0;
      }));
      setValores(novos);
    } catch (e: any) {
      setErro('Erro ao carregar: ' + (e.response?.data?.erro || e.message));
    } finally {
      setCarregando(false);
    }
  }

  function iniciarEdicao(trim: string, key: string) {
    const val = valores[trim]?.[key] ?? 0;
    setEditando({ trim, key });
    setInputVal(val === 0 ? '' : String(val));
  }

  async function confirmarEdicao() {
    if (!editando) return;
    const { trim, key } = editando;
    const num = parseFloat(inputVal.replace(',', '.')) || 0;
    const stateKey = `${trim}_${key}`;
    setEditando(null);
    setSaveState(prev => ({ ...prev, [stateKey]: 'saving' }));

    const novosVals = { ...valores, [trim]: { ...(valores[trim] || {}), [key]: num } };
    const derivados = calcularDerivados(novosVals, grupoCats, trim);

    setValores({ ...novosVals, [trim]: { ...novosVals[trim], ...derivados } });

    try {
      const token = await getToken();
      await Promise.all([
        axios.post(`${API_URL}/orcamento`, { trimestre: `${ano}/${trim}`, linha_dre: key, label: key, valor_orcado: num }, { headers: { Authorization: token } }),
        ...Object.entries(derivados).map(([k, v]) =>
          axios.post(`${API_URL}/orcamento`, { trimestre: `${ano}/${trim}`, linha_dre: k, label: k, valor_orcado: v }, { headers: { Authorization: token } })
        ),
      ]);
      setSaveState(prev => ({ ...prev, [stateKey]: 'ok' }));
      setTimeout(() => setSaveState(prev => ({ ...prev, [stateKey]: 'idle' })), 1500);
    } catch {
      setSaveState(prev => ({ ...prev, [stateKey]: 'err' }));
    }
  }

  function celula(q: string, chave: string, cor?: string) {
    const stateKey = `${q}_${chave}`;
    const isEditing = editando?.trim === q && editando?.key === chave;
    const val = valores[q]?.[chave] ?? 0;
    return (
      <td key={q}
        onClick={() => !isEditing && iniciarEdicao(q, chave)}
        style={{
          textAlign: 'right', padding: '5px 10px', fontSize: 12, cursor: 'pointer', minWidth: 110,
          background: saveState[stateKey] === 'ok' ? '#D4DEFF' : saveState[stateKey] === 'err' ? '#ffe0e0' : 'transparent',
          transition: 'background 0.3s',
        }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onBlur={confirmarEdicao}
            onKeyDown={e => { if (e.key === 'Enter') confirmarEdicao(); if (e.key === 'Escape') setEditando(null); }}
            style={{ width: 90, textAlign: 'right', border: '1px solid #0930D0', borderRadius: 4, padding: '2px 4px', fontSize: 12, outline: 'none' }}
            placeholder="0"
          />
        ) : (
          <span style={{ color: val === 0 ? '#ccc' : cor ?? (val > 0 ? '#0930D0' : '#E24B4A') }}>
            {val === 0 ? '—' : fmt(val)}
          </span>
        )}
      </td>
    );
  }

  const totalAnual = (key: string) => TRIMESTRES.reduce((s, q) => s + (valores[q]?.[key] ?? 0), 0);

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1140, margin: '0 auto' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 16 }}>
        <Space>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Orçamento</Title>
          <Select
            value={ano}
            onChange={setAno}
            options={[2025, 2026, 2027].map(a => ({ value: a, label: String(a) }))}
            size="small"
            style={{ width: 100 }}
          />
          {carregando && <Spin size="small" />}
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Clique para editar · salvo automaticamente
        </Text>
      </Flex>

      {erro && (
        <Alert
          type="error"
          message={erro}
          showIcon
          style={{ marginBottom: 12 }}
          closable
          onClose={() => setErro('')}
        />
      )}

      <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr style={{ background: '#020D7F', color: '#fff' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 500 }}>Linha DRE</th>
              {TRIMESTRES.map(q => (
                <th key={q} style={{ textAlign: 'right', padding: '10px 10px', fontSize: 12, fontWeight: 500, minWidth: 110 }}>
                  {LABELS_TRIM[q]}
                </th>
              ))}
              <th style={{ textAlign: 'right', padding: '10px 10px', fontSize: 12, fontWeight: 500, minWidth: 110 }}>Total {ano}</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: '#FAFBFC', borderBottom: '1px solid #f0f0f0' }}>
              <td colSpan={6} style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Receita Bruta
              </td>
            </tr>
            {AREAS_RECEITA.map(area => (
              <tr key={area.key} style={{ borderBottom: '1px solid #F5F7FA', background: '#fff' }}>
                <td style={{ padding: '5px 12px 5px 28px', fontSize: 12, color: '#444' }}>{area.label}</td>
                {TRIMESTRES.map(q => celula(q, area.key, '#0930D0'))}
                <td style={{ textAlign: 'right', padding: '5px 10px', fontSize: 12, color: '#888' }}>
                  {totalAnual(area.key) === 0 ? '—' : fmt(totalAnual(area.key))}
                </td>
              </tr>
            ))}
            <ResultRow label="= Receita Bruta Total" valores={valores} calcKey="recBruta" />

            {GRUPOS_ORDEM.map(({ grupo, label }) => {
              const cats = grupoCats[grupo] || [];
              const resultado = RESULTADO_APOS[grupo];
              return (
                <React.Fragment key={grupo}>
                  <tr style={{ background: '#FAFBFC', borderBottom: '1px solid #f0f0f0', borderTop: '1px solid #e0e0e0' }}>
                    <td colSpan={6} style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {label}
                    </td>
                  </tr>
                  {cats.length === 0 ? (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 12px 5px 28px', fontSize: 12, color: '#bbb', fontStyle: 'italic' }}>Sem categorias mapeadas</td>
                      {TRIMESTRES.map(q => <td key={q} style={{ padding: '5px 10px', textAlign: 'right', color: '#ccc', fontSize: 12 }}>—</td>)}
                      <td style={{ padding: '5px 10px', textAlign: 'right', color: '#ccc', fontSize: 12 }}>—</td>
                    </tr>
                  ) : cats.map(linha => (
                    <tr key={linha} style={{ borderBottom: '1px solid #F5F7FA', background: '#fff' }}>
                      <td style={{ padding: '5px 12px 5px 28px', fontSize: 12, color: '#444' }}>{linha}</td>
                      {TRIMESTRES.map(q => celula(q, linha))}
                      <td style={{ textAlign: 'right', padding: '5px 10px', fontSize: 12, color: '#888' }}>
                        {totalAnual(linha) === 0 ? '—' : fmt(totalAnual(linha))}
                      </td>
                    </tr>
                  ))}
                  {resultado && (
                    <ResultRow label={resultado.label} valores={valores} calcKey={resultado.key} />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 12 }}>
        Use valores negativos para despesas (ex: -200000) e positivos para receitas.
        As linhas em azul são calculadas automaticamente e salvas para exibição no comparativo "vs Orçamento" na DRE.
      </Text>
    </div>
  );
}

export default function Orcamento() {
  return (
    <AntApp>
      <OrcamentoInner />
    </AntApp>
  );
}
